import React from 'react'
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
  } from "@/components/ui/breadcrumb"
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table" 
import { CheckIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from "@/components/ui/separator"
import { Route as singleItemInventory } from '@/routes/(inventory)/branch/_layout/inventory/item/$type.$itemId'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { convertToCurrencyRounded } from '@/utils/currency_converter'
import dayjs from 'dayjs'
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { Loading } from '@/layouts/loading'
import EditItem from './edit-item'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles';
import { cn } from '@/lib/utils';
import { ItemType } from '@/gen/inventory/common_pb'
import { ItemLedgerSheet } from './item-ledger'
import StockPointSplitSheet from './stock-point-split'

// TableCell and TableHead wrappers for consistent styling
const ItemTableCell = ({ className = '', ...props }) => (
  <TableCell className={cn(tableCellClass, 'text-sm font-medium', className)} {...props} />
);
const ItemTableHead = ({ className = '', ...props }) => (
  <TableHead className={cn(tableHeadClass, className)} {...props} />
);

const Component = () => {
    const searchParams = singleItemInventory.useSearch();
    const pathParams = singleItemInventory.useParams();
    const data = singleItemInventory.useLoaderData();
    const navigate = useNavigate();

    return (
        <div className='h-full overflow-auto'>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => {
                            navigate({
                                to: '/branch/inventory'
                            })
                        }} style={{
                            cursor: 'pointer'
                        }}>Inventory Overview</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => {
                            navigate({
                                to: '/branch/inventory/available-inventory',
                                search: searchParams
                            })
                        }} style={{
                            cursor: 'pointer'
                        }}>All Inventory</BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>{data.medicalMaster ? data.medicalMaster.tradeName : data.nonMedicalMaster?.tradeName}</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <hr className='my-4' />

            {pathParams.type === 'MEDICAL' ? <MedicalItemComponent /> : <NonMedicalItemComponent />}
            
        </div>
    )
}

const MedicalItemComponent = () => {
    const data = singleItemInventory.useLoaderData();
    const router = useRouter()
    const search = singleItemInventory.useSearch();
    const pathParams = singleItemInventory.useParams();
    const [pricingLoading, setPricingLoading] = React.useState(false)
    const navigate = useNavigate()
    return (
        <div className='overflow-auto'>
        <div className='grid grid-cols-8 space-x-4 h-full'>
                <div className='col-span-5 p-4 overflow-auto max-h-[calc(100vh-150px)]'>
                    <div>
                        <div className='flex items-center justify-between'>
                            <div>
                                <p className="font-semibold">Your Inventory</p>
                                <p className="text-gray-500">
                                    Stock info in relation with your Pharmacy
                                </p>
                            </div>
                            <div>
                                <Select value={
                                    search.available ? "AVAILABLE" : search.expired ? "EXPIRED" : search.expiring_in_30_days ? "EXPIRING_IN_30_DAYS" : "OUT_OF_STOCK"
                                } onValueChange={(e) => {
                                    router.navigate({
                                        to: `/branch/inventory/item/${pathParams.type}/${pathParams.itemId}`,
                                        search: {
                                            ...search,
                                            available: e === "AVAILABLE",
                                            expired: e === "EXPIRED",
                                            expiring_in_30_days: e === "EXPIRING_IN_30_DAYS",
                                            out_of_stock: e === "OUT_OF_STOCK",
                                        }
                                    })
                                }}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder="Availability" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectItem value="AVAILABLE">Available</SelectItem>
                                            <SelectItem value="EXPIRED">Expired</SelectItem>
                                            <SelectItem value="EXPIRING_IN_30_DAYS">Expiring in 30 Days</SelectItem>
                                            <SelectItem value="OUT_OF_STOCK">Out of Stock</SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <hr className='my-2' />
                        <div className="border rounded-lg bg-white overflow-hidden mt-4">
                          <Table className='text-sm'>
                              <TableHeader>
                                  <TableRow>
                                      <ItemTableHead>Batch</ItemTableHead>
                                      <ItemTableHead>Stock</ItemTableHead>
                                      <ItemTableHead>Quantity</ItemTableHead>
                                      <ItemTableHead>Bonus</ItemTableHead>
                                      <ItemTableHead>Cost</ItemTableHead>
                                      <ItemTableHead>Expiry</ItemTableHead>
                                      {/* <ItemTableHead>Last Sold On</ItemTableHead> */}
                                      <ItemTableHead></ItemTableHead>
                                  </TableRow>
                              </TableHeader>
                              <TableBody>
                                  {data.itemType === ItemType.MEDICAL && data.inventoryData.items.map((item, index) => (
                                    <TableRow key={index} style={{
                                        opacity: item.availableQuantity === 0 || dayjs(item.expiryDate?.toDate()).isBefore(dayjs()) ? 0.5 : 1,
                                        cursor: item.availableQuantity === 0 || dayjs(item.expiryDate?.toDate()).isBefore(dayjs()) ? 'not-allowed' : 'default'
                                    }}>
                                        <ItemTableCell className="py-1">{item.batchNumber}</ItemTableCell>
                                        <ItemTableCell className="py-1">{item.availableQuantity}</ItemTableCell>
                                        <ItemTableCell className="py-1">{item.inputQuantity?.original}</ItemTableCell>
                                        <ItemTableCell className="py-1">{item.inputQuantity?.bonus}</ItemTableCell>
                                        <ItemTableCell className="py-1">{convertToCurrencyRounded(item.costFromSupplier)}</ItemTableCell>
                                        <ItemTableCell className="py-1">{dayjs(item.expiryDate?.toDate()).format("DD MMM YYYY")}</ItemTableCell>
                                        {/* <ItemTableCell className="py-1">{item.last_sold_date ? dayjs(item.last_sold_date).format("DD/MM/YYYY") : "Not Available"}</ItemTableCell> */}
                                        <ItemTableCell className="py-1">
                                            {/* <OfferItem inventory_id={item.inventory_id} type='MEDICAL' existingOffers={item.offers}/> */}
                                            <EditItem itemId={item.inventoryId} type={data.itemType} level={
                                                search.available ? "AVAILABLE" : search.expired ? "EXPIRED" : search.expiring_in_30_days ? "EXPIRING" : "OUT_OF_STOCK"
                                            }
                                                disabled={item.availableQuantity === 0 || dayjs(item.expiryDate?.toDate()).isBefore(dayjs())}
                                            />
                                            <ItemLedgerSheet itemId={item.inventoryId} itemType={item.itemType}/>
                                            <StockPointSplitSheet itemId={item.inventoryId} type={item.itemType}/>
                                        </ItemTableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                          </Table>
                        </div>
                        <hr className='my-4 border-t-2' />
                        <div className='flex items-center justify-between'>
                            <div>
                                <p className="font-semibold">Past price updates</p>
                                <p className="text-gray-500">
                                    Ministry Pricing Updates
                                </p>
                            </div>
                            <Button variant={'secondary'}>
                                Request for a Price Update
                            </Button>
                        </div>
                        <hr className='my-4' />
                        <div className='flex flex-col space-y-8'>
                            {data.medicalPricingHistory?.map((price, index, array) => (
                                <div className='flex gap-x-4' key={index}>
                                    <p className='text-gray-600'>{dayjs(price.effectiveDate?.toDate()).format("DD MMM YYYY")}</p>
                                    <Separator className='mx-4 h-[120px] w-1 bg-gray-500' orientation="vertical" />
                                    <div>
                                        <p className='font-semibold text-gray-700'>{convertToCurrencyRounded(price.price)} {index < array.length - 1 ? `| ${price.price - array[index+1].price > 0 ? 'Increased By' : 'Decreased By'} ${convertToCurrencyRounded(price.price - array[index+1].price)}` : null}</p>
                                        <p className='text-gray-600 text-sm'>Applicable Areas: {price.associatedStates.join(", ")}</p>
                                        {price.isCurrentActivePriceForPharmacy ? (
                                            <div className='flex items-center space-x-4 mt-4'>
                                                <p className='text-gray-600 text-sm'>Current Active Price for your Pharmacy <br /> Effective from {dayjs(price.effectiveDate?.toDate()).format("DD MMM YYYY")}</p>
                                            </div>
                                        ) : (
                                            !data.medicalPricingHistory?.find(p => p.masterPriceHistoryId === price.masterPriceHistoryId) ? (
                                                <div className='flex items-center space-x-4 mt-4'>
                                                    <Button
                                                        variant={'outline'}
                                                        className='text-blue-700'
                                                        disabled={pricingLoading}
                                                    >
                                                        <CheckIcon className='w-4 h-4 mr-2' />
                                                        {pricingLoading ? <Loading /> : "Accept price change"}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className='flex items-center space-x-4 mt-4'>
                                                    <p className='text-gray-600 text-sm'>Past Active Price <br />Effective From {dayjs(data.medicalPricingHistory?.find(p => p.masterPriceHistoryId === array[index-1].masterPriceHistoryId)?.effectiveDate?.toDate()).format("DD MMM YYYY")} to {dayjs(data.medicalPricingHistory.find(p => p.masterPriceHistoryId === array[index-1].masterPriceHistoryId)?.effectiveDate?.toDate()).format("DD MMM YYYY")}</p>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className='col-span-3 p-4 bg-gray-400/10 rounded-lg overflow-auto max-h-[calc(100vh-150px)]'>
                    <p className="font-semibold">Item Details</p>
                    <p className="text-gray-500">
                        Details acquired from Government
                    </p>
                    <hr className='my-2' />
                    <div className='text-sm'>
                        <p className='text-gray-500'>Trade Name</p>
                        <p className='font-semibold'>{data.medicalMaster?.tradeName}</p>
                    </div>
                    <hr className='my-2' />
                    <div className='text-sm'>
                        <p className='text-gray-500'>Scientific Name</p>
                        <p className='font-semibold'>{data.medicalMaster?.metadata?.scientificName}</p>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Barcode</p>
                            <p className='font-semibold underline'>{data.medicalMaster?.barcode || "N/A"}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>DDC Code</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.ddcCode}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Last updated by DDC</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.ddcLastUpdated}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Ingredient</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.ingredient}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Manufacturer</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.manufacturer}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Owner</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.owner}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Source</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.source}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Strength</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.strength}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Unit</p>
                            <p className='font-semibold'>{data.medicalMaster?.unit}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Unit Type</p>
                            <p className='font-semibold'>{data.medicalMaster?.unitType}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Route of Administration</p>
                            <p className='font-semibold'>{data.medicalMaster?.metadata?.routeOfAdministration}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Tax</p>
                            <p className='font-semibold'>{data.medicalMaster?.taxProfile?.applicableTaxes.filter(item => {
                                        const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                                        if (countryCode === 'IN') {
                                            return item.componentCode.includes('GST');
                                        } else if (countryCode === 'AE') {
                                            return item.componentCode.includes('VAT');
                                        }
                                        return false;
                                    }).map(a => {
                                        return `${a.componentCode} (${a.ratePercent}%)`;
                                    })}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                </div>
            </div>
            <hr className='my-4 border-t-2' />
            <div className='h-full'>
                <div className='flex items-center justify-between py-4'>
                    <p className='font-semibold'>Monthly Stock Report</p>
                    <div className='flex items-center space-x-4'>
                        <Select value={search.year.toString()} onValueChange={(e) => {
                            navigate({
                                search: {
                                    ...search,
                                    year: parseInt(e)
                                }
                            })
                        }}>
                            <SelectTrigger className="w-[280px]">
                                <SelectValue placeholder="Select a year" />
                            </SelectTrigger>
                            <SelectContent>
                                {[...Array(12).keys()].map((_, index) => {
                                    const year = new Date().getFullYear() - 10 + index;
                                    return (
                                        <SelectGroup key={year}>
                                            <SelectItem value={year.toString()}>{year}</SelectItem>
                                        </SelectGroup>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="border rounded-lg bg-white overflow-hidden mt-4">
                  <Table className='text-sm'>
                      <TableHeader>
                          <TableRow>
                              <ItemTableHead>Month</ItemTableHead>
                              <ItemTableHead>Opening Stock</ItemTableHead>
                              <ItemTableHead>Net Movement in Month</ItemTableHead>
                              <ItemTableHead>Closing Stock</ItemTableHead>
                          </TableRow>
                      </TableHeader>
                      <TableBody>
                          {Array.from({ length: 12 }, (_, month) => {
                              const report = data.stockMovementReport.items.find(item => item.month === month + 1);
                              return (
                                  <TableRow key={month}>
                                      <ItemTableCell>{dayjs().month(month).format("MMMM")} {search.year}</ItemTableCell>
                                      <ItemTableCell>{report ? (report.openingStock !== null ? report.openingStock : 'No Data') : 'No Data'}</ItemTableCell>
                                      <ItemTableCell>{report ? (report.netMovement !== null ? report.netMovement : 'No Data') : 'No Data'}</ItemTableCell>
                                      <ItemTableCell>{report ? (report.closingStock !== null ? report.closingStock : 'No Data') : 'No Data'}</ItemTableCell>
                                  </TableRow>
                              );
                          })}
                      </TableBody>
                  </Table>
                </div>
            </div>
            </div>
    )
}

const NonMedicalItemComponent = () => {
    const data = singleItemInventory.useLoaderData();
    const search = singleItemInventory.useSearch();
    const pathParams = singleItemInventory.useParams();
    const router = useRouter();
    const navigate = useNavigate();
    return (
        <div>
        <div className='grid grid-cols-8 space-x-4 h-full'>
                <div className='col-span-5 p-4 overflow-auto max-h-[calc(100vh-150px)]'>
                    <div>
                        <div className='flex items-center justify-between'>
                            <div>
                                <p className="font-semibold">Your Inventory</p>
                                <p className="text-gray-500">
                                    Stock info in relation with your Pharmacy
                                </p>
                            </div>
                            <div>
                                <Select value={
                                    search.available ? "AVAILABLE" : search.expired ? "EXPIRED" : search.expiring_in_30_days ? "EXPIRING_IN_30_DAYS" : "OUT_OF_STOCK"
                                } onValueChange={(e) => {
                                    router.navigate({
                                        to: `/branch/inventory/item/${pathParams.type}/${pathParams.itemId}`,
                                        search: {
                                            ...search,
                                            available: e === "AVAILABLE",
                                            expired: e === "EXPIRED",
                                            expiring_in_30_days: e === "EXPIRING_IN_30_DAYS",
                                            out_of_stock: e === "OUT_OF_STOCK",
                                        }
                                    })
                                }}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder="Availability" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectItem value="AVAILABLE">Available</SelectItem>
                                            <SelectItem value="EXPIRED">Expired</SelectItem>
                                            <SelectItem value="EXPIRING_IN_30_DAYS">Expiring in 30 Days</SelectItem>
                                            <SelectItem value="OUT_OF_STOCK">Out of Stock</SelectItem>
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <hr className='my-2' />
                        <div className="border rounded-lg bg-white overflow-hidden mt-4">
                          <Table className='text-sm'>
                              <TableHeader>
                                  <TableRow>
                                      <ItemTableHead>Batch</ItemTableHead>
                                      <ItemTableHead>Stock</ItemTableHead>
                                      <ItemTableHead>Quantity</ItemTableHead>
                                      <ItemTableHead>Bonus</ItemTableHead>
                                      <ItemTableHead>Price</ItemTableHead>
                                      <ItemTableHead>Cost</ItemTableHead>
                                      <ItemTableHead>Expiry</ItemTableHead>
                                      {/* <ItemTableHead>Last Sold On</ItemTableHead> */}
                                      <ItemTableHead></ItemTableHead>
                                  </TableRow>
                              </TableHeader>
                              <TableBody>
                                  {data.itemType === ItemType.NON_MEDICAL && data.inventoryData.items.map((item, index) => (
                                    <TableRow key={index} style={{
                                        opacity: item.availableQuantity === 0 || item.isValidExpiryDate ? dayjs(item.expiryDate?.toDate()).isBefore(dayjs()) ? 0.5 : 1 : 1,
                                        cursor: item.availableQuantity === 0 || item.isValidExpiryDate ? dayjs(item.expiryDate?.toDate()).isBefore(dayjs()) ? 'not-allowed' : 'default' : 'default'
                                    }}>
                                        <ItemTableCell className="py-1">{item.batchNumber}</ItemTableCell>
                                        <ItemTableCell className="py-1">{item.availableQuantity}</ItemTableCell>
                                        <ItemTableCell className="py-1">{item.inputQuantity?.original}</ItemTableCell>
                                        <ItemTableCell className="py-1">{item.inputQuantity?.bonus}</ItemTableCell>
                                        <ItemTableCell className="py-1">{convertToCurrencyRounded(item.sellingPrice)}</ItemTableCell>
                                        <ItemTableCell className="py-1">{convertToCurrencyRounded(item.costFromSupplier)}</ItemTableCell>
                                        <ItemTableCell className="py-1">{item.isValidExpiryDate ? dayjs(item.expiryDate?.toDate()).format("DD/MM/YYYY") : "No Expiry"}</ItemTableCell>
                                        {/* <ItemTableCell className="py-1">{item.last_sold_date ? dayjs(item.last_sold_date).format("DD/MM/YYYY") : "Not Available"}</ItemTableCell> */}
                                        <ItemTableCell className="py-1">
                                            {/* <OfferItem inventory_id={item.inventoryId} type='NON_MEDICAL' existingOffers={item.offers}/> */}
                                            <EditItem itemId={item.inventoryId} type={data.itemType} level={
                                                search.available ? "AVAILABLE" : search.expired ? "EXPIRED" : search.expiring_in_30_days ? "EXPIRING" : "OUT_OF_STOCK"
                                            }
                                                disabled={item.availableQuantity === 0 || dayjs(item.expiryDate?.toDate()).isBefore(dayjs())}
                                            />
                                            <ItemLedgerSheet itemId={item.inventoryId} itemType={item.itemType}/>
                                            <StockPointSplitSheet itemId={item.inventoryId} type={item.itemType}/>
                                        </ItemTableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                          </Table>
                        </div>
                    </div>
                </div>

                <div className='col-span-3 p-4 bg-gray-400/10 rounded-lg overflow-auto max-h-[calc(100vh-150px)]'>
                    <p className="font-semibold">Item Details</p>
                    <p className="text-gray-500">
                        Details entered during item creation
                    </p>
                    <hr className='my-2' />
                    <div className='text-sm'>
                        <p className='text-gray-500'>Trade Name</p>
                        <p className='font-semibold'>{data.nonMedicalMaster?.tradeName}</p>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Category</p>
                            <p className='font-semibold'>{data.nonMedicalMaster?.metadata?.category}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Sub Category</p>
                            <p className='font-semibold'>{data.nonMedicalMaster?.metadata?.subCategory}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Manufacturer</p>
                            <p className='font-semibold'>{data.nonMedicalMaster?.metadata?.manufacturer}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Owner</p>
                            <p className='font-semibold'>{data.nonMedicalMaster?.metadata?.owner}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Unit</p>
                            <p className='font-semibold'>{data.nonMedicalMaster?.unit}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Unit Type</p>
                            <p className='font-semibold'>{data.nonMedicalMaster?.unitType}</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                    <div className='grid grid-cols-2 space-x-4'>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Barcode</p>
                            <p className='font-semibold underline'>{data.nonMedicalMaster?.barcode ? data.nonMedicalMaster?.barcode : "N/A"}</p>
                        </div>
                        <div className='text-sm'>
                            <p className='text-gray-500'>Tax</p>
                            <p className='font-semibold'>{
                                data.nonMedicalMaster?.taxProfile?.applicableTaxes.filter(item => {
                                    const countryCode = localStorage.getItem('pharmacyMetadata') ? JSON.parse(localStorage.getItem('pharmacyMetadata') || '{}').countryCode : 'IN';
                                    if (countryCode === 'IN') {
                                        return item.componentCode.includes('GST');
                                    } else if (countryCode === 'AE') {
                                        return item.componentCode.includes('VAT');
                                    }
                                    return false;
                                }).map(a => {
                                    return `${a.componentCode} (${a.ratePercent}%)`;
                                }).join(', ')
                                }</p>
                        </div>
                    </div>
                    <hr className='my-2' />
                </div>
            </div>
            <hr className='my-4 border-t-2' />
            <div className='h-full'>
                <div className='flex items-center justify-between py-4'>
                    <p className='font-semibold'>Monthly Stock Report</p>
                    <div className='flex items-center space-x-4'>
                        <Select value={search.year.toString()} onValueChange={(e) => {
                            navigate({
                                search: {
                                    ...search,
                                    year: parseInt(e)
                                }
                            })
                        }}>
                            <SelectTrigger className="w-[280px]">
                                <SelectValue placeholder="Select a year" />
                            </SelectTrigger>
                            <SelectContent>
                                {[...Array(12).keys()].map((_, index) => {
                                    const year = new Date().getFullYear() - 10 + index;
                                    return (
                                        <SelectGroup key={year}>
                                            <SelectItem value={year.toString()}>{year}</SelectItem>
                                        </SelectGroup>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="border rounded-lg bg-white overflow-hidden mt-4">
                  <Table className='text-sm'>
                      <TableHeader>
                          <TableRow>
                              <ItemTableHead>Month</ItemTableHead>
                              <ItemTableHead>Opening Stock</ItemTableHead>
                              <ItemTableHead>Closing Stock</ItemTableHead>
                          </TableRow>
                      </TableHeader>
                      <TableBody>
                         {Array.from({ length: 12 }, (_, month) => {
                              const report = data.stockMovementReport.items.find(item => item.month === month + 1);
                              return (
                                  <TableRow key={month}>
                                      <ItemTableCell>{dayjs().month(month).format("MMMM")} {search.year}</ItemTableCell>
                                      <ItemTableCell>{report ? (report.openingStock !== null ? report.openingStock : 'No Data') : 'No Data'}</ItemTableCell>
                                      <ItemTableCell>{report ? (report.netMovement !== null ? report.netMovement : 'No Data') : 'No Data'}</ItemTableCell>
                                      <ItemTableCell>{report ? (report.closingStock !== null ? report.closingStock : 'No Data') : 'No Data'}</ItemTableCell>
                                  </TableRow>
                              );
                          })}
                      </TableBody>
                  </Table>
                </div>
            </div>
            </div>
    )
}

export default Component